// «Олноор нэмэх» input: one line per item, `code, quantity` separated by a comma,
// semicolon or tab (a copy from Excel arrives tab-separated). Empty quantity = 1.

export const MAX_MATCH_ROWS = 500

export type InputRow = {
  /** 1-based line number in the source text. */
  line: number
  code: string
  quantity: number
  /** The quantity cell held something other than a whole number >= 1. */
  invalid: boolean
}

const SPLIT = /[,;\t]/

export function parseLine(raw: string, line: number): InputRow | null {
  const [codeCell = "", qtyCell = ""] = raw.split(SPLIT).map((c) => c.trim())
  if (!codeCell) return null
  if (!qtyCell) return { line, code: codeCell, quantity: 1, invalid: false }
  const quantity = Number(qtyCell)
  const invalid = !Number.isInteger(quantity) || quantity < 1
  return { line, code: codeCell, quantity: invalid ? 0 : quantity, invalid }
}

export function parseLines(text: string): InputRow[] {
  return text
    .split(/\r?\n/)
    .map((raw, i) => parseLine(raw, i + 1))
    .filter((r): r is InputRow => r !== null)
}

export const sameCode = (a: string, b: string) => a.trim().toLowerCase() === b.trim().toLowerCase()

export const serializeRows = (rows: { code: string; quantity: number }[]) =>
  rows.map((r) => `${r.code}, ${r.quantity}`).join("\n")

/** Barcode mode: a repeated scan adds one to that line, a new code appends a line with 1. */
export function appendCode(text: string, code: string): string {
  const rows = parseLines(text).filter((r) => !r.invalid)
  const existing = rows.find((r) => sameCode(r.code, code))
  if (existing) existing.quantity += 1
  else rows.push({ line: rows.length + 1, code, quantity: 1, invalid: false })
  return serializeRows(rows)
}

/**
 * Sheet rows (first row = header) to input text. The code column is the one
 * headed SKU / код / barcode, else the first; quantity is the column headed
 * тоо / qty / quantity, else the second.
 */
export function sheetToText(matrix: unknown[][]): string {
  if (matrix.length === 0) return ""
  const header = (matrix[0] || []).map((c) => String(c ?? "").trim().toLowerCase())
  const find = (re: RegExp, fallback: number) => {
    const i = header.findIndex((h) => re.test(h))
    return i >= 0 ? i : fallback
  }
  const codeCol = find(/sku|код|code|barcode|баркод/, 0)
  const qtyCol = find(/тоо|qty|quantity|ширхэг/, 1)
  return matrix
    .slice(1)
    .map((r) => {
      const code = String(r?.[codeCol] ?? "").trim()
      const qty = String(r?.[qtyCol] ?? "").trim()
      return code ? (qty ? `${code}, ${qty}` : code) : ""
    })
    .filter(Boolean)
    .join("\n")
}
